'use client';

import { useEffect, useRef } from 'react';
import { ChevronRight } from 'lucide-react';
import {
  getCategoryLabel,
  naceToCategory,
  naceToSubcategory,
} from '@/lib/industry-categories';
import {
  generateCategoryInternalPath,
  generateIndustryUrl,
} from '@/lib/industry-utils';
import { useLocale } from '@/hooks/use-locale';
import { Container } from '@/components/common/container';
import { LocaleLink } from '@/components/locale-link';

type Props = {
  primaryNace: string | null;
  primaryNaceDescription?: string | null;
  companyName: string;
};

/** Timestamp of the last history back/forward. A mount right after popstate is
 * Next restoring a scroll position — the auto-scroll must not fight it. */
let lastPopstateAt = 0;
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    lastPopstateAt = Date.now();
  });
}

/** A NACE code is "deeper than the keyword hierarchy" when it has a group/class/
 * subclass component (i.e., more than just the 2-digit division). The keyword
 * hierarchy only covers levels 1-2, so anything level 3+ gets its own
 * breadcrumb step linking to the canonical /industry/<cat>/<sub>/<code> URL. */
function isDeepNaceCode(code: string | null): boolean {
  if (!code) return false;
  const digits = code.replace(/\D/g, '');
  return digits.length > 2;
}

/**
 * Client-side breadcrumb for company detail pages.
 * Recomputes locale-specific labels + category slugs on every language switch
 * so the nav updates without a page reload. The server-rendered JSON-LD
 * BreadcrumbList is emitted separately for SEO.
 */
export function CompanyBreadcrumbs({
  primaryNace,
  primaryNaceDescription,
  companyName,
}: Props) {
  const { locale } = useLocale();
  const navRef = useRef<HTMLElement>(null);

  // Start the viewport at the company title: scroll the breadcrumb row (and
  // its container padding) under the sticky header. A single scroll at mount
  // is not enough on client-side navigations — the effect races the route
  // transition (Next's own scroll-to-top, the search page's body-scroll lock
  // being released, streamed-in content), so the position is enforced for a
  // short window instead and re-derived from live layout every frame. Skipped
  // entirely when a scroll position is being restored (deep link, history
  // back/forward, reload with a saved position) or once the user touches the
  // page.
  useEffect(() => {
    if (window.location.hash) return;
    if (Date.now() - lastPopstateAt < 1000) return;
    const navTiming = performance.getEntriesByType(
      'navigation',
    )[0] as PerformanceNavigationTiming | undefined;
    // Reload / back-forward full loads may restore a scroll position while
    // the document is still settling (only treat this mount as part of that
    // load when it happens close to it). There, keep the original one-shot
    // behavior: scroll once if the page is at the top, and never fight a
    // restore that lands later.
    const restoringLoad =
      navTiming &&
      navTiming.type !== 'navigate' &&
      performance.now() - navTiming.domContentLoadedEventEnd < 3000;
    if (restoringLoad) {
      if (window.scrollY > 0) return;
      const container = navRef.current?.parentElement;
      if (!container) return;
      const headerHeight =
        document.querySelector<HTMLElement>('.fd-header')?.offsetHeight ?? 0;
      const top =
        container.getBoundingClientRect().bottom +
        window.scrollY -
        headerHeight;
      if (top > 0) window.scrollTo({ top, behavior: 'instant' });
      return;
    }

    let cancelled = false;
    let raf = 0;
    const stop = () => {
      cancelled = true;
      cancelAbortListeners();
      cancelAnimationFrame(raf);
    };
    // Any user input hands scroll control back immediately.
    const abortEvents = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
    const cancelAbortListeners = () => {
      abortEvents.forEach((e) => window.removeEventListener(e, stop));
      window.removeEventListener('popstate', stop);
    };
    abortEvents.forEach((e) =>
      window.addEventListener(e, stop, { passive: true }),
    );
    window.addEventListener('popstate', stop);

    const deadline = performance.now() + 1500;
    const step = () => {
      if (cancelled) return;
      const container = navRef.current?.parentElement;
      if (!container) return stop();
      const headerHeight =
        document.querySelector<HTMLElement>('.fd-header')?.offsetHeight ?? 0;
      const top = Math.round(
        container.getBoundingClientRect().bottom +
          window.scrollY -
          headerHeight,
      );
      if (top > 0 && Math.abs(window.scrollY - top) > 1) {
        window.scrollTo({ top, behavior: 'instant' });
      }
      if (performance.now() < deadline) {
        raf = requestAnimationFrame(step);
      } else {
        stop();
      }
    };
    raf = requestAnimationFrame(step);
    return stop;
  }, []);

  const companiesLabel = locale === 'de' ? 'Unternehmen' : 'Companies';
  const catKey = naceToCategory(primaryNace);
  const subKey = naceToSubcategory(primaryNace);

  const items: { name: string; href?: string }[] = [
    { name: companiesLabel, href: '/search' },
  ];
  if (catKey) {
    items.push({
      name: getCategoryLabel(catKey, locale),
      href: generateCategoryInternalPath(catKey, locale),
    });
  }
  if (subKey && subKey !== catKey) {
    items.push({
      name: getCategoryLabel(subKey, locale),
      href: generateCategoryInternalPath(subKey, locale),
    });
  }
  if (isDeepNaceCode(primaryNace) && primaryNaceDescription) {
    items.push({
      name: primaryNaceDescription,
      href: generateIndustryUrl(primaryNace!, primaryNaceDescription, locale),
    });
  }
  items.push({ name: companyName });

  return (
    <Container className="pt-4 pb-2">
      <nav
        ref={navRef}
        className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground"
        aria-label="Breadcrumb"
      >
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <span key={index} className="flex items-center gap-1">
              {index > 0 && (
                <ChevronRight size={14} className="text-muted-foreground/50" />
              )}
              {isLast || !item.href ? (
                <span
                  className="text-foreground font-medium"
                  {...(isLast && { 'aria-current': 'page' })}
                >
                  {item.name}
                </span>
              ) : (
                <LocaleLink
                  href={item.href}
                  className="hover:text-foreground transition-colors"
                >
                  {item.name}
                </LocaleLink>
              )}
            </span>
          );
        })}
      </nav>
    </Container>
  );
}
